<template>
  <div class="validator-holder" v-if="true">
    <GridPanelHeader
      :title="$t('tools.import_validator.title')"
      :grid-ref="$refs.importValidatorGrid"
      :show-add="false"
      :show-filter="false"
    >
      <template #back_button_action>
        <DxButton class="back-button" icon="arrowleft" @click="goBack" />
      </template>
    </GridPanelHeader>
    <DxDataGrid
      id="gridContainer"
      keyExpr="uuid"
      :ref="gridRefKey"
      :data-source="gridData"
      :columns="gridColumns"
      :element-attr="gridAttributes"
      :show-column-lines="true"
      :show-row-lines="true"
      :show-borders="true"
      :row-alternation-enabled="false"
      :filter-row="{ visible: true, applyFilter: 'auto' }"
      :selection="{ mode: 'multiple', showCheckBoxesMode: 'always' }"
      :column-min-width="50"
      :selected-row-keys="selectedRowKeys"
      :allow-column-reordering="true"
      :column-auto-width="true"
      :repaint-changes-only="true"
      :focused-row-enabled="true"
      @selection-changed="onSelectionChanged"
      @saving="onSaving"
      @exporting="onExporting"
      @row-prepared="onRowPrepared"
      @editor-preparing="onEditorPreparing"
      @option-changed="onOptionChanged"
      @initialized="onInitialized"
      @content-ready="onContentReady"
      @focused-row-changing="onFocusedRowChanging"
      @row-validating="onRowValidating"
    >
      <DxHeaderFilter :visible="true" />
      <DxPaging :enabled="true" :pageSize="100" />
      <DxPager display-mode="compact" :visible="true" :showPageSizeSelector="true" :allowedPageSizes="[23, 100, 500]" :showInfo="true" :info-text="$t('common.page_info')" />
      <DxSearchPanel :visible="true" :width="240" />
      <DxSelection select-all-mode="allMode" show-check-boxes-mode="always" mode="multiple" />
      <DxLoadPanel :enabled="true" :show-indicator="true" :show-pane="true" :shading="true" />
      <DxEditing
        :allow-updating="true"
        :allow-adding="false"
        :allow-deleting="true"
        :select-text-on-edit-start="true"
        :start-edit-action="'click'"
        mode="batch"
        :use-icons="true"
      />
      <DxScrolling column-rendering-mode="virtual" />
      <DxGroupPanel :visible="true" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxExport :enabled="true" :allow-export-selected-data="true" :fileName="'ValidatorDataGridExport'" />
      <DxColumnChooser :enabled="true" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxToolbar>
        <DxItem name="groupPanel" />
        <DxItem name="revertButton" />
        <DxItem name="saveButton" />
        <DxItem name="searchPanel" />
        <DxItem
          location="before"
          widget="dxButton"
          :disabled="false"
          :options="{
            icon: 'trash',
            text: 'Delete selected rows (' + selectedCount + ')',
            onClick: confirmDelete,
            height: 30,
          }"
        />
        <DxItem
          location="before"
          widget="dxButton"
          :disabled="false"
          :options="{
            icon: 'edit',
            text: 'Edit selected rows (' + selectedCount + ')',
            onClick: onClickShowEditModal,
            height: 30,
          }"
        />
        <DxItem
          location="before"
          widget="dxButton"
          :disabled="false"
          :options="{
            icon: 'clear',
            text: 'Clear Selection (' + selectedCount + ')',
            onClick: clearSelection,
            height: 30,
          }"
        />
      </DxToolbar>
    </DxDataGrid>
    <DeleteConfirmationDialog
      :show="showDeleteConfirmationModal"
      @close="showDeleteConfirmationModal = false"
      @confirm="deleteSelectedRows"
      :title="$t('tools.import_validator.title')"
      :message="$t('tools.import_validator.delete_from_validated_data')"
    ></DeleteConfirmationDialog>
    <MultipleRowsEditDialog
      :show="showEditModal"
      @close="showEditModal = false"
      @confirm="editSelectedRows"
      :departments="this.getCellDepartments()"
      :seasons="this.getCellSeasons()"
      :work_process="this.getUniqueWorkProcess()"
      :person="this.getUniquePerson()"
      :batch_measure_units="this.getCellBatchMeasureUnits()"
      :title="$t('tools.import_validator.title')"
    >
    </MultipleRowsEditDialog>
    <CreateParcel
      :isModal="true"
      :show="showParcelDetails"
      :defaultNewValues="defaultValuesForNewParcel"
      @close="showParcelDetails = false"
      @newParcelCreated="newParcelCreatedResult"
    ></CreateParcel>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxScrolling,
  DxLoadPanel,
  DxDataGridTypes,
  DxToolbar,
  DxItem,
  DxPaging,
  DxPager,
  DxSearchPanel,
  DxEditing,
  DxHeaderFilter,
  DxGroupPanel,
  DxExport,
  DxColumnChooser,
  DxColumnFixing,
  DxSelection,
} from "devextreme-vue/data-grid";

import { DxButton } from "devextreme-vue/button";
import { successMessage, errorMessage, errorMessageList } from "~/helpers/common";
import { getValidatorGridColumns } from "./gridColumns";
import { mapActions, mapGetters } from "vuex";
import { format } from "date-fns";
import DeleteConfirmationDialog from "@/components/Modals/DeleteConfirmationDialog";
import MultipleRowsEditDialog from "./MultipleRowsEditDialog";
import CreateParcel from "~/pages/works/parcels/_id.vue";
import { saveDxGridColumnSettings, loadDxGridColumnSettings } from "@/helpers/dxGridHelper";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "ImportValidator",
  components: {
    DxDataGrid,
    DxScrolling,
    DxLoadPanel,
    DxDataGridTypes,
    DxToolbar,
    DxItem,
    DxButton,
    DxPaging,
    DxPager,
    DxSearchPanel,
    DxEditing,
    DxHeaderFilter,
    DxGroupPanel,
    DxExport,
    DxColumnChooser,
    DxColumnFixing,
    DxSelection,
    DeleteConfirmationDialog,
    MultipleRowsEditDialog,
    CreateParcel,
    GridPanelHeader,
  },
  data() {
    return {
      gridRefKey: "importValidatorGrid",
      selectedRowKeys: [],
      selectedCount: 0,
      gridData: this.importValidatedData ? this.importValidatedData : [],
      showDeleteConfirmationModal: false,
      showEditModal: false,
      gridColumns: [],
      batchMeasureUnits: [],
      showParcelDetails: false,
      defaultValuesForNewParcel: {},
      selectedRowData: {},
      visibilitySettingsLoaded: false,
      visibilitySettingsLoading: false,
      saveColumnSettingsTimeout: null,
      parcelFilters: {},
      workProcesses: [],
      activeLoadingOperations: 0,
    };
  },
  computed: {
    gridAttributes() {
      return {
        class: "import-validator-grid",
      };
    },
    ...mapGetters({
      importValidatedData: "importConfig/importValidatedData",
      seasons: "seasons/seasons",
      departments: "department/departments",
      importValidatedDataSettings: "importConfig/importValidatedDataSettings",
    }),
    dataGridInstance: function () {
      const gridRef = this.$refs[this.gridRefKey];
      return gridRef && gridRef.instance ? gridRef.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getImportValidatedData: "importConfig/getImportValidatedData",
      updateImportValidatedData: "importConfig/updateImportValidatedData",
      getSeasons: "seasons/getSeasons",
      getDepartments: "department/getDepartments",
      getBatchMeasureUnits: "batch/getBatchMeasureUnits",
      getParcels: "works-parcels/getParcels",
      getDxGridSettings: "dxgrid/getDxGridSettings",
      setDxGridSettings: "dxgrid/setDxGridSettings",
      getImportValidatedDataSettings: "importConfig/getImportValidatedDataSettings",
      getWorks: "works-manager/getWorks",
    }),
    async loadImportValidatedData(data_import_id) {
      this.getImportValidatedData({ data_import_id: data_import_id });
    },
    getGridColumns() {
      const columns = getValidatorGridColumns(
        this.startCreateParcel,
        this.getLookupDataSource,
        this.importValidatedDataSettings["parcel_full_identification"] ?? 0,
      );
      columns.map((column) => {
        if (column.caption) {
          column.caption = this.$t("tools.import_validator." + column.caption);
        }
        return column;
      });

      return columns;
    },
    formatDateColumns(changes) {
      changes.forEach((change) => {
        if (change.data) {
          if (change.data.start_date) {
            change.data.start_date = format(new Date(change.data.start_date), "yyyy-MM-dd HH:mm:ss");
          }
          if (change.data.end_date) {
            change.data.end_date = format(new Date(change.data.end_date), "yyyy-MM-dd HH:mm:ss");
          }
        }
      });
    },
    beginGridLoading(message = null) {
      if (!this.dataGridInstance) {
        return;
      }

      this.activeLoadingOperations += 1;
      if (message) {
        this.dataGridInstance.beginCustomLoading(message);
      } else {
        this.dataGridInstance.beginCustomLoading();
      }
    },
    endGridLoading() {
      if (!this.dataGridInstance || this.activeLoadingOperations === 0) {
        return;
      }

      this.activeLoadingOperations -= 1;
      if (this.activeLoadingOperations === 0) {
        this.dataGridInstance.endCustomLoading();
      }
    },
    async runWithGridLoading(callback, message = null) {
      this.beginGridLoading(message);
      try {
        return await callback();
      } finally {
        this.endGridLoading();
      }
    },
    async updateImportValidatedDataWithCallback(changes) {
      try {
        const response = await this.updateImportValidatedData({ changes });
        successMessage(this, response.message);
        return true;
      } catch (e) {
        errorMessageList(this, e.errors);
        return false;
      }
    },
    onSaving(e) {
      if (e.changes.length) {
        this.formatDateColumns(e.changes);
        this.runWithGridLoading(async () => {
          const result = await this.updateImportValidatedDataWithCallback(e.changes);
          if (result) {
            e.component.refresh();
          }
        });
      }
    },
    async confirmDelete() {
      if (this.selectedRowKeys?.length > 0) {
        this.showDeleteConfirmationModal = true;
      }
    },
    async deleteSelectedRows() {
      this.showDeleteConfirmationModal = false;
      if (this.selectedRowKeys.length > 0) {
        const changes = [];
        this.selectedRowKeys.forEach((key) => {
          changes.push({ type: "remove", key: key });
        });

        await this.runWithGridLoading(async () => {
          const result = await this.updateImportValidatedDataWithCallback(changes);
          if (result) {
            if (this.dataGridInstance) {
              const dataSource = this.dataGridInstance.getDataSource();
              if (dataSource) {
                const store = dataSource.store();
                if (store) {
                  for (const key of this.selectedRowKeys) {
                    try {
                      store.remove(key);
                    } catch (error) {
                      console.error(`Failed to remove item with key ${key}:`, error);
                    }
                  }
                }
                dataSource.reload();
              }
            }
            this.clearSelection();
          }
        });
      }
    },
    onSelectionChanged(e) {
      this.selectedRowKeys = e.selectedRowKeys;
      this.selectedCount = e.selectedRowKeys.length;
    },
    onExporting(e) {
      e.fileName = "SGPS_DASC_DataGridExport_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      //const dataRows = e.component.getDataSource().items();
      //e.cancel = true; // Prevent default export

      if (e.format === "xlsx") {
        const fields = [];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SGPS_DASC_DATA");
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            // Check if the column is "weighing_type_id"
            if (fields.includes(gridCell?.column?.dataField) && excelCell.row > 1) {
              // excelCell.value = this.getYesNoColumnText(!!gridCell.data[gridCell?.column?.dataField]);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    onRowPrepared(event) {
      if (event.rowType === "data") {
        const rowData = event.data;
        let backgroundColor;
        if (rowData.status && rowData.status.color) {
          event.rowElement.style.backgroundColor = rowData.status.color;
          backgroundColor = rowData.status.color;
        }
        if (backgroundColor) {
          event.rowElement.style.backgroundColor = backgroundColor;
          event.rowElement.style.color = this.getContrastingTextColor(backgroundColor);
        }
      }
    },
    onEditorPreparing(e) {
      if (this.importValidatedDataSettings["parcel_full_identification"] ?? 0) {
        if (e.row && e.row.data && e.dataField === "parcel_name") {
          this.setNewDataSourceBasedOnSelectionForParcel(e.row.data);
        }

        const parcel_fields = ["season_uuid", "surface", "culture", "department_uuid"];
        if (e.row && e.row.data && parcel_fields.includes(e.dataField) && e.row.data.parcel_uuid) {
          e.editorOptions.disabled = true;
          errorMessage(this, this.$t("tools.import_validator.parcel_is_already_created_for_this_row"));
        }
      } else {
        const parcel_fields = ["parcel_name", "season_uuid", "surface", "culture", "department_uuid"];
        if (e.row && e.row.data && parcel_fields.includes(e.dataField) && e.row.data.parcel_uuid) {
          e.editorOptions.disabled = true;
          errorMessage(this, this.$t("tools.import_validator.parcel_is_already_chosen_for_this_row_by_remote_identification"));
        }
      }
    },
    onOptionChanged(e) {
      saveDxGridColumnSettings(this, e);
    },
    onContentReady(e) {
      loadDxGridColumnSettings(this);
    },
    onFocusedRowChanging(e) {},
    onInitialized(e) {},
    clearSelection() {
      this.selectedRowKeys = [];
    },
    startCreateParcel(e_row_data) {
      if (e_row_data.parcel_uuid) {
        this.selectedRowData = null;
        return;
      }

      this.defaultValuesForNewParcel = {
        department_uuid: e_row_data.department_uuid,
        season_uuid: e_row_data.season_uuid,
        name: e_row_data.parcel_name,
        surface: parseFloat(e_row_data.surface ?? 0),
        culture: e_row_data.culture,
      };

      this.selectedRowData = e_row_data;
      this.showParcelDetails = true;
    },
    newParcelCreatedResult(parcel) {
      const rowIndex = this.dataGridInstance.getRowIndexByKey(this.selectedRowData.uuid);
      if (rowIndex !== -1 && this.dataGridInstance) {
        this.dataGridInstance.beginUpdate();
        this.dataGridInstance.cellValue(rowIndex, "parcel_name", parcel.name);
        this.dataGridInstance.cellValue(rowIndex, "parcel_uuid", parcel.uuid);
        this.dataGridInstance.cellValue(rowIndex, "culture", parcel.culture);
        this.dataGridInstance.cellValue(rowIndex, "surface", parcel.surface);
        this.dataGridInstance.cellValue(rowIndex, "department_uuid", parcel.department_uuid);
        this.dataGridInstance.cellValue(rowIndex, "season_uuid", parcel.season_uuid);

        const parcelName = parcel.name.toLowerCase();
        const rowsToUpdate = this.gridData.filter((row) => {
          if (row.parcel_name && parcelName && (row.parcel_uuid == null || row.parcel_uuid == "")) {
            const rowParcelName = row.parcel_name.toLowerCase();
            if (rowParcelName == parcelName || rowParcelName.localeCompare(parcelName) === 0) {
              return true;
            }
          }
          return false;
        });
        rowsToUpdate.forEach((row) => {
          const rowIndex = this.dataGridInstance.getRowIndexByKey(row.uuid);
          if (rowIndex !== -1) {
            this.dataGridInstance.cellValue(rowIndex, "parcel_name", parcel.name);
            this.dataGridInstance.cellValue(rowIndex, "parcel_uuid", parcel.uuid);
            this.dataGridInstance.cellValue(rowIndex, "culture", parcel.culture);
            this.dataGridInstance.cellValue(rowIndex, "surface", parcel.surface);
            this.dataGridInstance.cellValue(rowIndex, "department_uuid", parcel.department_uuid);
            this.dataGridInstance.cellValue(rowIndex, "season_uuid", parcel.season_uuid);
          }
        });

        this.dataGridInstance.endUpdate();
        this.dataGridInstance.refresh();
      }

      this.showParcelDetails = false;
    },
    goBack() {
      this.$router.back();
    },
    getCellDepartments() {
      return (
        this.departments
          /*.filter(department => department.type === 'company')*/
          .map((value) => ({
            department: value.name,
            department_uuid: value.uuid,
          }))
      );
    },
    getCellWorkProcesses() {
      return this.workProcesses.map((value) => ({
        work_process: value.name,
        work_process_uuid: value.uuid,
      }));
    },
    getCellSeasons() {
      return this.seasons.map((value) => ({
        season: value.name,
        season_uuid: value.uuid,
      }));
    },
    getCellBatchMeasureUnits() {
      return this.batchMeasureUnits.map((value) => ({
        product_measure_unit: value.name,
        product_measure_unit_uuid: value.uuid,
      }));
    },
    getUniqueWorkProcess() {
      const work_process = [...new Set(this.gridData.map((value) => value.work_process))];
      return work_process.map((process, index) => ({
        value: index + 1,
        label: process,
      }));
    },
    getUniquePerson() {
      const person = [...new Set(this.gridData.map((value) => value.person))];
      return person.map((person, index) => ({
        value: index + 1,
        label: person,
      }));
    },
    getUniqueParcelNames() {
      const parcel_names = [...new Set(this.gridData.map((value) => value.parcel_name))];
      return parcel_names.map((parcel_name, index) => ({
        value: parcel_name,
        text: parcel_name,
      }));
    },
    onClickShowEditModal() {
      if (this.dataGridInstance.getSelectedRowKeys().length > 0) {
        this.showEditModal = true;
      }
    },
    async editSelectedRows(department_uuid, season_uuid, work_process, person, product_measure_unit_uuid) {
      this.showEditModal = false;

      const selectedRowKeys = this.dataGridInstance.getSelectedRowKeys();
      const dataSource = this.dataGridInstance.getDataSource();

      if (
        selectedRowKeys.length === 0 ||
        !dataSource ||
        (!department_uuid && !season_uuid && !work_process && !person && !product_measure_unit_uuid)
      ) {
        return;
      }

      const store = dataSource.store();
      if (!store) {
        return;
      }

      let rowMap = null;
      if (this.importValidatedDataSettings["parcel_full_identification"] ?? 0) {
        rowMap = new Map(this.gridData.map((r) => [r.uuid, r]));
      }

      const changes = [];
      let not_updated_rows = false;
      selectedRowKeys.map((key) => {
        let parcel_selected = false;
        if ((this.importValidatedDataSettings["parcel_full_identification"] ?? 0) && rowMap) {
          const row = rowMap.get(key);
          if (row && !!row.parcel_uuid) {
            parcel_selected = true;
          }
        }

        let updateItemData = {};
        if (department_uuid) {
          if (parcel_selected) {
            not_updated_rows = true;
          } else {
            updateItemData.department_uuid = department_uuid;
          }
        }
        if (season_uuid) {
          if (parcel_selected) {
            not_updated_rows = true;
          } else {
            updateItemData.season_uuid = season_uuid;
          }
        }
        if (work_process) {
          updateItemData.work_process = work_process;
        }
        if (person) {
          updateItemData.person = person;
        }
        if (product_measure_unit_uuid) {
          updateItemData.product_measure_unit_uuid = product_measure_unit_uuid;
        }
        if (Object.keys(updateItemData).length > 0) {
          changes.push({ type: "update", key: key, data: updateItemData });
        }
      });

      if (changes.length === 0) {
        if (not_updated_rows) {
          errorMessage(this, this.$t("tools.import_validator.some_rows_not_updated_due_to_existing_parcel"));
        }
        return;
      }

      await this.runWithGridLoading(async () => {
        const result = await this.updateImportValidatedDataWithCallback(changes);
        if (!result) {
          return;
        }

        // Update the data in bulk using the store
        const updatePromises = selectedRowKeys.map((key) => {
          return store.byKey(key).then((rowData) => {
            let parcel_selected = false;
            if (this.importValidatedDataSettings["parcel_full_identification"] ?? 0) {
              if (rowData.parcel_uuid) {
                parcel_selected = true;
              }
            }

            if (rowData) {
              if (department_uuid && !parcel_selected) {
                rowData.department_uuid = department_uuid;
              }
              if (season_uuid && !parcel_selected) {
                rowData.season_uuid = season_uuid;
              }
              if (work_process) {
                rowData.work_process = work_process;
              }
              if (person) {
                rowData.person = person;
              }
              if (product_measure_unit_uuid) {
                rowData.product_measure_unit_uuid = product_measure_unit_uuid;
              }
              return store.update(key, rowData);
            }

            return null;
          });
        });

        try {
          // Wait for all updates to complete
          await Promise.all(updatePromises);
          // Refresh the DataGrid to reflect the changes
          this.dataGridInstance.refresh();

          if (not_updated_rows) {
            errorMessage(this, this.$t("tools.import_validator.some_rows_not_updated_due_to_existing_parcel"));
          }
        } catch (error) {
          console.error("Error updating rows:", error);
        }
      });
    },
    setNewDataSourceBasedOnSelectionForParcel(selectedRowsData) {
      if (
        this.parcelFilters.season_uuid == selectedRowsData.season_uuid &&
        this.parcelFilters.department_uuid == selectedRowsData.department_uuid
      ) {
        this.updateLookupDataSource(
          "parcel_name",
          this.getFilteredPercelsByName(this.getLookupDataSource("parcel_name") ?? [], selectedRowsData.parcel_name),
        );
        return;
      }

      this.parcelFilters.season_uuid = selectedRowsData.season_uuid;
      this.parcelFilters.department_uuid = selectedRowsData.department_uuid;
      this.parcelFilters.limit = 10000;
      this.updateLookupDataSource("parcel_name", []);

      this.getParcels(this.parcelFilters).then((result) => {
        let parcels = result.data;
        if (selectedRowsData.parcel_name && selectedRowsData.parcel_name.trim() !== "") {
          parcels = this.getFilteredPercelsByName(parcels, selectedRowsData.parcel_name);
        }
        this.updateLookupDataSource("parcel_name", parcels);
      });
    },
    getFilteredPercelsByName(parcels, parcel_name) {
      if (!parcel_name || parcel_name.trim() === "") {
        return parcels;
      }

      parcel_name = parcel_name?.replace(/(\w+)-\d+,\d+-(.*)/, "$1 $2").replace(/(\w+)-(.*)/, "$1") ?? "";
      parcels.map((parcel, key) => {
        if (parcel?.name?.replace(/(\w+)-\d+,\d+-(.*)/, "$1 $2").replace(/(\w+)-(.*)/, "$1") == parcel_name) {
          parcels[key]["level"] = 0;
        } else {
          parcels[key]["level"] = 1;
        }
      });
      return parcels.sort((a, b) => a.level - b.level);
    },
    getContrastingTextColor(backgroundColor) {
      const color = backgroundColor.charAt(0) === "#" ? backgroundColor.substring(1, 7) : backgroundColor;
      const r = parseInt(color.substring(0, 2), 16);
      const g = parseInt(color.substring(2, 4), 16);
      const b = parseInt(color.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? "#000000" : "#FFFFFF"; // Black text for light backgrounds, white text for dark backgrounds
    },
    getLookupDataSource(dataField) {
      if (this.dataGridInstance) {
        return this.dataGridInstance.columnOption(dataField, "lookup.dataSource");
      }
      return null;
    },
    updateLookupDataSource(datField, newDataSource, editorSource = "lookup.dataSource") {
      if (this.dataGridInstance) {
        this.dataGridInstance.columnOption(datField, editorSource, newDataSource);
      }
    },
    onRowValidating(event) {
      const brokenRules = event.brokenRules;
      if (brokenRules.length > 0) {
        const error_messages = [];
        brokenRules.forEach((rule) => {
          if ((rule.type === "required" || rule.type === "custom") && rule.message) {
            if (!error_messages.includes(rule.message)) {
              error_messages.push(rule.message);
            }
          }
        });

        if (error_messages.length > 0) {
          event.isValid = false;
          errorMessage(this, error_messages.join(", "));
        }
      }
    },
    loadWorks() {
      this.getWorks({
        enablePagination: 0,
        limit: 9999,
      }).then((response) => {
        this.workProcesses = response.data;
        if (this.gridData && Array.isArray(this.gridData)) {
          this.gridData.forEach((row) => {
            if (row.work_process && this.workProcesses && Array.isArray(this.workProcesses)) {
              const found = this.workProcesses.find(
                (wp) =>
                  wp.name &&
                  row.work_process &&
                  wp?.name?.toLowerCase()?.replace(/\s+/g, "") === row.work_process?.toLowerCase()?.replace(/\s+/g, ""),
              );
              if (found) {
                row.work_process_uuid = found.uuid;
                row.work_process = found.name;
              } else {
                // If not found, add the work_process to workProcesses
                const uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 11);
                this.workProcesses.push({
                  name: row.work_process,
                  uuid: uuid,
                });

                row.work_process_uuid = uuid; // Set to null if not found
              }
            }
          });
        }
        this.updateLookupDataSource("work_process", this.getCellWorkProcesses(), "editorOptions.dataSource");
      });
    },
  },
  async mounted() {
    if (this.$route.query.import_id) {
      this.getImportValidatedDataSettings({ data_import_id: this.$route.query.import_id });

      this.getDepartments();
      this.getSeasons();
      this.getBatchMeasureUnits().then((result) => {
        this.batchMeasureUnits = result;
        this.updateLookupDataSource("product_measure_unit_uuid", this.getCellBatchMeasureUnits());
      });
      this.loadImportValidatedData(this.$route.query.import_id);
    }
  },
  watch: {
    importValidatedData: {
      handler: function (newVal, oldVal) {
        this.gridData = newVal;
        this.loadWorks();
      },
      deep: true,
    },
    departments: {
      handler: function (newVal, oldVal) {
        this.updateLookupDataSource("department_uuid", this.getCellDepartments());
      },
      deep: true,
    },
    seasons: {
      handler: function (newVal, oldVal) {
        this.updateLookupDataSource("season_uuid", this.getCellSeasons());
      },
      deep: true,
    },
    importValidatedDataSettings: {
      handler: function (newVal, oldVal) {
        this.gridColumns = this.getGridColumns();
      },
      deep: true,
    },
  },
};
</script>

<style>
.import-validator-grid {
  /*height: 960px;*/
  height: calc(95vh - 130px);
}

.selected-data {
  margin-top: 20px;
  padding: 17px;
  background-color: rgba(191, 191, 191, 0.15);
}

​ .selected-data .caption {
  font-weight: bold;
  font-size: 115%;
  margin-right: 4px;
}

.import-action-btn {
  margin-left: 20px;
  margin-top: -6px;
  float: right;
}

.disabled-btn {
  background-color: gray !important;
  cursor: not-allowed;
}

.dx-button-has-icon .dx-button-content {
  padding: 1px;
}

/* Set the row height to 40px */
#gridContainer .dx-datagrid-rowsview .dx-row {
  height: 40px;
}

/* Use a softer selection/focus color for this page's grid rows */
.import-validator-grid .dx-datagrid-rowsview .dx-row.dx-selection > td,
.import-validator-grid .dx-datagrid-rowsview .dx-row-focused > td {
  background-color: #d7ebff !important;
  color: #1f2d3d !important;
}

.add-parcel-button {
  margin-right: 10px;
}

.validator-status-icon {
  font-size: 20px;
  color: #c0c0c0;
}
</style>
